import React, { useState, useEffect, useRef } from 'react';
import { useConversation } from '@11labs/react';
import { getAgentConfig } from '../config/agent';

interface DemoConfig {
  id: string;
  title: string;
  description: string;
  industry: string;
  icon: React.ReactNode;
  gradient: string;
  agentId: string;
  conversations: {
    agent: string;
    user: string;
  }[];
  persona: {
    name: string;
    role: string;
    company: string;
  };
}

const defaultAgentId = getAgentConfig().agentId;

const demos: DemoConfig[] = [
  {
    id: 'restaurant',
    title: 'Restaurant Orders',
    description: 'AI handles customer orders and inquiries',
    industry: 'Food & Beverage',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    gradient: 'from-orange-500 via-red-500 to-pink-500',
    agentId: defaultAgentId,
    persona: {
      name: 'Tony',
      role: 'Order Assistant',
      company: "Mario's Pizzeria"
    },
    conversations: [
      {
        agent: "Hi! Welcome to Mario's Pizzeria! I'm Tony, your AI assistant. How can I help you today?",
        user: "Hi! I'd like to order a pizza for delivery."
      },
      {
        agent: "Perfect! I'd be happy to help you with that. What size pizza were you thinking, and what toppings would you like?",
        user: "I'll take a large pepperoni pizza."
      },
      {
        agent: "Excellent choice! One large pepperoni pizza. That'll be $18.99. Can I get your delivery address and phone number to complete the order?",
        user: "Sure, it's 123 Main Street, and my number is 555-0123."
      },
      {
        agent: "Perfect! I have your order for one large pepperoni pizza for $18.99, delivering to 123 Main Street. Your order will be ready in about 25-30 minutes. Is there anything else I can get for you today?",
        user: "That's all, thank you!"
      }
    ]
  },
  {
    id: 'realestate',
    title: 'Real Estate Sales',
    description: 'AI assists with property inquiries and showings',
    industry: 'Real Estate',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    gradient: 'from-blue-500 via-cyan-500 to-teal-500',
    agentId: 'agent_01jymrtyajedprcfaygp22pve4',
    persona: {
      name: 'Sarah',
      role: 'Sales Agent',
      company: 'Premier Realty'
    },
    conversations: [
      {
        agent: "Hello! This is Sarah from Premier Realty. I understand you're interested in viewing some properties. How can I help you today?",
        user: "Yes, I'm looking for a 3-bedroom house in the downtown area."
      },
      {
        agent: "Great! I have several beautiful 3-bedroom properties in downtown. What's your budget range, and are there any specific features you're looking for?",
        user: "Our budget is around $400,000, and we'd prefer a house with a garage and a backyard."
      },
      {
        agent: "Perfect! I have three properties that match your criteria. There's a lovely Victorian-style home at $395,000 with a 2-car garage and beautiful landscaped backyard. Would you like to schedule a viewing this week?",
        user: "That sounds perfect! When are you available for a showing?"
      },
      {
        agent: "I have availability tomorrow at 2 PM or Friday at 10 AM. Which works better for you? I'll also send you the property details and photos via email before our meeting.",
        user: "Friday at 10 AM works great!"
      }
    ]
  },
  {
    id: 'healthcare',
    title: 'Medical Scheduling',
    description: 'AI manages appointments and patient inquiries',
    industry: 'Healthcare',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
    gradient: 'from-green-500 via-emerald-500 to-teal-500',
    agentId: 'agent_01jymnwqb4f5tbsza65whht1h1',
    persona: {
      name: 'Rebecca',
      role: 'Patient Coordinator',
      company: 'Wellness Medical Center'
    },
    conversations: [
      {
        agent: "Good morning! This is Rebecca from Wellness Medical Center. How may I assist you today?",
        user: "Hi, I need to schedule an appointment with Dr. Johnson."
      },
      {
        agent: "I'd be happy to help you schedule with Dr. Johnson. Is this for a routine check-up or do you have a specific concern you'd like to address?",
        user: "It's for my annual physical exam."
      },
      {
        agent: "Perfect! Dr. Johnson has availability next Tuesday at 9:30 AM or Thursday at 2:15 PM. Which time works better for your schedule?",
        user: "Tuesday at 9:30 AM would be perfect."
      },
      {
        agent: "Excellent! I've scheduled your annual physical with Dr. Johnson for Tuesday at 9:30 AM. Please arrive 15 minutes early to complete any paperwork. Is the phone number we have on file still the best way to reach you?",
        user: "Yes, that's still my current number. Thank you!"
      }
    ]
  },
  {
    id: 'support',
    title: 'Tech Support',
    description: 'AI provides technical assistance and troubleshooting',
    industry: 'Technology',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    gradient: 'from-purple-500 via-violet-500 to-indigo-500',
    agentId: defaultAgentId,
    persona: {
      name: 'Alex',
      role: 'Tech Support Specialist',
      company: 'CloudTech Solutions'
    },
    conversations: [
      {
        agent: "Hello! This is Alex from CloudTech Solutions technical support. I'm here to help you with any technical issues you're experiencing. What can I assist you with today?",
        user: "Hi, I'm having trouble connecting to my WiFi network."
      },
      {
        agent: "I understand how frustrating connection issues can be. Let's get this sorted out for you. First, can you tell me what device you're trying to connect and what happens when you try to connect?",
        user: "It's my laptop, and it keeps saying 'unable to connect to network' even though I'm entering the correct password."
      },
      {
        agent: "Thank you for that information. Let's try a few troubleshooting steps. First, can you restart your WiFi router by unplugging it for 30 seconds, then plugging it back in?",
        user: "Okay, I've done that. Should I try connecting again now?"
      },
      {
        agent: "Yes, please try connecting again. If that doesn't work, we'll try forgetting the network on your laptop and reconnecting fresh. This usually resolves authentication issues. How did the reconnection go?",
        user: "It worked! I'm connected now. Thank you so much!"
      }
    ]
  }
];

const MultiDemo: React.FC = () => {
  const [activeDemo, setActiveDemo] = useState(demos[0]);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [messages, setMessages] = useState<Array<{text: string, sender: 'user' | 'agent', timestamp: number}>>([]);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [currentConversationIndex, setCurrentConversationIndex] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isCalling, setIsCalling] = useState(false);
  const [heroPhoneNumber, setHeroPhoneNumber] = useState('');
  const [heroIsCalling, setHeroIsCalling] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const agentConfig = getAgentConfig();

  // Auto-scroll chat container to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Reset conversation when demo changes
  useEffect(() => {
    setMessages([]);
    setCurrentConversationIndex(0);
    setIsDemoMode(false);
  }, [activeDemo]);

  const conversation = useConversation({
    onConnect: () => {
      console.log('Connected to Crel AI agent');
    },
    onDisconnect: () => {
      console.log('Disconnected from Crel AI agent');
    },
    onMessage: (message) => {
      console.log('Message received:', message);
      if (message.source === 'user' && message.message) {
        setMessages(prev => [...prev, {
          text: message.message,
          sender: 'user',
          timestamp: Date.now()
        }]);
      } else if (message.source === 'ai' && message.message) {
        setMessages(prev => [...prev, {
          text: message.message,
          sender: 'agent',
          timestamp: Date.now()
        }]);
      }
    },
    onError: (error) => {
      console.error('Conversation error:', error);
    }
  });

  const requestMicrophonePermission = async () => {
    setIsRequestingPermission(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setIsPermissionGranted(true);
    } catch (error) {
      console.error('Microphone permission denied:', error);
      alert('Microphone access is required for the voice agent demo. Please allow microphone access and try again.');
    } finally {
      setIsRequestingPermission(false);
    }
  };

  const startConversation = async () => {
    try {
      const demoAgentId = activeDemo.agentId;
      if (demoAgentId && demoAgentId !== 'your-agent-id-here' && demoAgentId !== 'sample-agent-id') {
        await conversation.startSession({
          agentId: demoAgentId
        });
      } else {
        throw new Error('Agent ID not configured');
      }
    } catch (error) {
      console.error('Failed to start conversation:', error);
      
      if (agentConfig.demoMode.ENABLED) {
        setIsDemoMode(true);
        setCurrentConversationIndex(0);
        playDemoConversation();
      }
    }
  };

  const playDemoConversation = () => {
    const conversations = activeDemo.conversations;
    setMessages([]);
    
    conversations.forEach((conv, index) => {
      // Agent messages
      setTimeout(() => {
        setMessages(prev => [...prev, {
          text: conv.agent,
          sender: 'agent',
          timestamp: Date.now()
        }]);
      }, index * 4000);

      // User messages (appear after agent messages)
      if (index < conversations.length - 1 || conv.user) {
        setTimeout(() => {
          setMessages(prev => [...prev, {
            text: conv.user,
            sender: 'user',
            timestamp: Date.now()
          }]);
        }, index * 4000 + 2000);
      }
    });
  };

  const endConversation = async () => {
    if (isDemoMode) {
      setIsDemoMode(false);
      setMessages([]);
      setCurrentConversationIndex(0);
    } else {
      await conversation.endSession();
      setMessages([]);
    }
  };

  // Hero section call function
  const callUserHero = async () => {
    if (!heroPhoneNumber) return;
    const defaultAgentId = demos[0].agentId; // Use restaurant demo agent as default
    
    if (defaultAgentId === 'your-actual-agent-id-here' || defaultAgentId === 'sample-agent-id') {
      alert('Configure a valid agent ID first.');
      return;
    }

    try {
      setHeroIsCalling(true);
      const response = await fetch('https://emievoctxtzblzddryxq.supabase.co/functions/v1/call-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId: defaultAgentId,
          toNumber: heroPhoneNumber
        })
      });

      if (!response.ok) throw new Error('Request failed');
      alert('Call initiated! Your AI agent will call you shortly.');
      setHeroPhoneNumber('');
    } catch (error) {
      console.error('Failed to initiate call:', error);
      alert('Failed to initiate call. Please try again.');
    } finally {
      setHeroIsCalling(false);
    }
  };

  // Initiate outbound call via ElevenLabs (placeholder implementation)
  const callUser = async () => {
    if (!phoneNumber) return;
    if (activeDemo.agentId === 'your-actual-agent-id-here' || activeDemo.agentId === 'sample-agent-id') {
      alert('Configure a valid agent ID first.');
      return;
    }

    try {
      setIsCalling(true);
      // TODO: Replace with your backend endpoint or direct ElevenLabs API call
      const response = await fetch('https://emievoctxtzblzddryxq.supabase.co/functions/v1/call-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId: activeDemo.agentId,
          toNumber: phoneNumber
        })
      });

      if (!response.ok) throw new Error('Request failed');
      alert('Outbound call initiated!');
      setPhoneNumber('');
    } catch (error) {
      console.error('Failed to initiate call:', error);
      alert('Failed to initiate call. Please try again.');
    } finally {
      setIsCalling(false);
    }
  };

  const { status, isSpeaking } = conversation;
  const isConnected = status === 'connected' || isDemoMode;

  return (
    <div className="relative max-w-7xl mx-auto mb-8">
            {/* Hero Call Me Section */}
      <div className="relative mb-12 overflow-hidden h-80 rounded-3xl">
        {/* Animated Background Visual - Corporate Colors */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/15 via-slate-700/10 to-cyan-600/15">
          {/* Radial gradient overlay */}
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/20 to-black/60"></div>
          
          {/* Large animated wave visualization */}
          <div className="absolute inset-0 flex items-center">
            <div className="flex items-center justify-between w-full h-full px-2">
              {[...Array(80)].map((_, i) => {
                const delay1 = i * 0.03;
                const delay2 = i * 0.05 + 1;
                const delay3 = i * 0.02 + 2;
                const progress = i / 79; // 0 to 1
                const centerDistance = Math.abs(progress - 0.5) * 2; // 0 at center, 1 at edges
                const envelope = 1 - Math.pow(centerDistance, 1.8); // Smooth taper from center
                
                // Create multiple overlapping wave patterns with bigger amplitudes
                const wave1 = Math.sin(i * 0.12) * 140;
                const wave2 = Math.sin(i * 0.18 + Math.PI/4) * 120;
                const wave3 = Math.sin(i * 0.08 + Math.PI/2) * 160;
                const wave4 = Math.sin(i * 0.22 + Math.PI) * 100;
                
                const combinedWave = (wave1 + wave2 + wave3 + wave4) / 4;
                const waveHeight = Math.abs(combinedWave) * envelope;
                const baseHeight = 20 + waveHeight;
                const maxHeight = 320;
                
                return (
                  <div
                    key={i}
                    className="w-1 bg-gradient-to-t from-blue-500/80 via-cyan-400/70 to-slate-300/80 rounded-full"
                    style={{
                      height: `${Math.max(Math.min(baseHeight, maxHeight), 10)}px`,
                      animation: `
                        wave 3.2s ease-in-out infinite ${delay1}s,
                        wave 4.1s ease-in-out infinite ${delay2}s,
                        wave 2.8s ease-in-out infinite ${delay3}s
                      `
                    }}
                  ></div>
                );
              })}
            </div>
          </div>
          
          {/* Concentric circles */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-32 h-32 border border-white/10 rounded-full animate-pulse"></div>
            <div className="absolute w-48 h-48 border border-white/5 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute w-64 h-64 border border-white/5 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
        </div>
        
        {/* Overlay Controls */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-6">
            <div className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto">
              <input
                type="tel"
                placeholder="Enter Phone Number"
                value={heroPhoneNumber}
                onChange={(e) => setHeroPhoneNumber(e.target.value)}
                className="flex-1 bg-black/30 backdrop-blur-xl border border-white/20 text-white px-4 py-3 rounded-xl placeholder-white/60 text-center focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent"
              />
              <button
                onClick={callUserHero}
                disabled={!heroPhoneNumber || heroIsCalling}
                className="bg-white text-black font-semibold px-6 py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 shadow-xl"
              >
                {heroIsCalling ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    <span>Calling...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a2 2 0 011.94 1.515l.7 2.804a2 2 0 01-.545 1.958l-1.028 1.028a11.042 11.042 0 005.292 5.292l1.028-1.028a2 2 0 011.958-.545l2.804.7A2 2 0 0121 14.72V18a2 2 0 01-2 2h-1C9.163 20 4 14.837 4 8V7a2 2 0 00-1-2z" />
                    </svg>
                    <span>Call Me</span>
                  </>
                )}
              </button>
            </div>
            <p className="text-white/70 text-xs mt-3">
              Free AI voice demo
            </p>
          </div>
        </div>
      </div>

       {/* Compact Header with Title */}
      <div className="text-center mb-6">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
          Try Our <span className="gradient-text">AI Voice Agents</span>
        </h2>
        <p className="text-gray-400 text-lg max-w-3xl mx-auto">
          Experience how our AI agents handle real customer interactions across different industries
        </p>
      </div>

      {/* Compact Demo Interface */}
      <div className="glass-card rounded-3xl overflow-hidden relative">
        {/* Demo Selector Tabs - Compact at Top */}
        <div className="relative z-10 p-4 border-b border-white/10">
          <div className="flex flex-wrap gap-2 justify-center">
            {demos.map((demo) => (
              <button
                key={demo.id}
                onClick={() => setActiveDemo(demo)}
                className={`group relative px-4 py-3 rounded-xl transition-all duration-300 flex items-center gap-3 ${
                  activeDemo.id === demo.id
                    ? `bg-gradient-to-r ${demo.gradient} text-white shadow-lg transform scale-105`
                    : 'bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/10 hover:border-white/20'
                }`}
              >
                {/* Icon */}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                  activeDemo.id === demo.id
                    ? 'bg-white/20'
                    : `bg-gradient-to-br ${demo.gradient} opacity-80`
                }`}>
                  <div className="w-5 h-5 text-current">
                    {demo.icon}
                  </div>
                </div>
                
                {/* Title */}
                <div className="hidden sm:block">
                  <div className="text-sm font-semibold">{demo.title}</div>
                  <div className={`text-xs opacity-70 ${
                    activeDemo.id === demo.id ? 'text-white/80' : 'text-gray-400'
                  }`}>
                    {demo.industry}
                  </div>
                </div>
                
                {/* Mobile: Just industry */}
                <div className="sm:hidden text-xs font-medium">
                  {demo.industry}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Animated Background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${activeDemo.gradient} opacity-5`}></div>
        <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${activeDemo.gradient}`}></div>
        
        {/* Agent Info Header */}
        <div className="relative z-10 px-6 py-4 border-b border-white/10 bg-black/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${activeDemo.gradient} flex items-center justify-center text-white shadow-lg`}>
                <div className="w-5 h-5">
                  {activeDemo.icon}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  {activeDemo.persona.name}
                  {isDemoMode && (
                    <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-medium rounded-full border border-yellow-500/30">
                      Demo
                    </span>
                  )}
                </h3>
                <p className="text-gray-400 text-sm">
                  {activeDemo.persona.role} at {activeDemo.persona.company}
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-2 px-3 py-2 bg-white/5 rounded-lg border border-white/10">
              <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${activeDemo.gradient} animate-pulse`}></div>
              <span className="text-white text-sm font-medium">{activeDemo.industry}</span>
            </div>
          </div>
        </div>
        
                 {/* Main Content Area - Compact Layout */}
         <div className="relative z-10 flex flex-col lg:flex-row h-[500px]">
          {/* Voice Visualization - Compact */}
          <div className="flex-1 flex flex-col items-center justify-center p-4 lg:border-r border-white/10">
            {/* Compact Central Orb */}
            <div className="relative mb-4">
              {/* Outer glow rings */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className={`w-28 h-28 rounded-full bg-gradient-to-r ${activeDemo.gradient} opacity-10 transition-all duration-1000 ${
                  isConnected ? 'animate-pulse scale-110' : ''
                }`}></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className={`w-24 h-24 rounded-full bg-gradient-to-r ${activeDemo.gradient} opacity-20 transition-all duration-700 ${
                  isConnected ? 'animate-pulse scale-105' : ''
                }`} style={{ animationDelay: '0.3s' }}></div>
              </div>
              
              {/* Main orb */}
              <div className={`relative w-20 h-20 rounded-full bg-gradient-to-r ${activeDemo.gradient} flex items-center justify-center transition-all duration-500 shadow-2xl ${
                isConnected ? 'scale-110' : ''
              }`}>
                {/* Inner glow */}
                <div className={`absolute inset-2 rounded-full bg-gradient-to-r ${activeDemo.gradient} opacity-60`}></div>
                
                {/* Microphone icon */}
                <svg className="relative z-10 w-8 h-8 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
                
                {/* Animated rings when connected */}
                {isConnected && (
                  <>
                    <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${activeDemo.gradient} animate-ping opacity-30`}></div>
                    <div className="absolute -inset-2 rounded-full border-2 border-white/40 animate-pulse"></div>
                    <div className="absolute -inset-4 rounded-full border border-white/30 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                  </>
                )}
              </div>
            </div>
            
            {/* Compact Audio Waveform */}
            <div className="flex items-end justify-center space-x-1 mb-4 h-12">
              {[...Array(12)].map((_, i) => {
                const height = isConnected && (isSpeaking || isDemoMode) 
                  ? Math.sin((Date.now() / 200) + (i * 0.5)) * 12 + 16
                  : 4;
                
                return (
                  <div 
                    key={i}
                    className={`w-1 bg-gradient-to-t ${activeDemo.gradient} rounded-full transition-all duration-150 ${
                      isConnected ? 'opacity-100' : 'opacity-40'
                    }`}
                    style={{
                      height: `${Math.max(height, 4)}px`,
                      animationDelay: `${i * 0.1}s`,
                      transform: isConnected && (isSpeaking || isDemoMode) ? 'scaleY(1)' : 'scaleY(0.3)'
                    }}
                  ></div>
                );
              })}
            </div>
            
            {/* Compact Status Display */}
            <div className="text-center mb-4">
              <div className="flex items-center justify-center mb-2">
                <div className={`w-2 h-2 rounded-full mr-2 transition-all duration-300 ${
                  isConnected 
                    ? `bg-gradient-to-r ${activeDemo.gradient} animate-pulse shadow-lg` 
                    : 'bg-gray-500'
                }`}></div>
                <span className="text-white text-sm font-semibold">
                  {isConnected 
                    ? (isSpeaking ? 'Speaking...' : isDemoMode ? 'Demo Active' : 'Listening...') 
                    : 'Ready'
                  }
                </span>
              </div>
              <p className="text-gray-400 text-xs">
                {isConnected ? 'AI assistance active' : 'Enable microphone to start'}
              </p>
            </div>
            
            {/* Compact Control Buttons */}
            <div className="space-y-3 w-full max-w-xs">
              {!isPermissionGranted ? (
                <button 
                  onClick={requestMicrophonePermission}
                  disabled={isRequestingPermission}
                  className={`w-full bg-gradient-to-r ${activeDemo.gradient} hover:shadow-lg text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg text-sm`}
                >
                  {isRequestingPermission ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span className="hidden sm:inline">Requesting...</span>
                      <span className="sm:hidden">...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 14.142M9 9a3 3 0 000 6v-6z" />
                      </svg>
                      Enable Mic
                    </>
                  )}
                </button>
              ) : !isConnected ? (
                <button 
                  onClick={startConversation}
                  className={`w-full bg-gradient-to-r ${activeDemo.gradient} hover:shadow-lg text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg text-sm`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  Start Demo
                </button>
              ) : (
                <button 
                  onClick={endConversation}
                  className="w-full bg-red-500/20 hover:bg-red-500/30 border-2 border-red-500/30 hover:border-red-500/50 text-red-400 font-semibold px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  End Demo
                </button>
              )}
            </div>
          </div>
          
          {/* Chat Messages - Compact */}
          <div className="flex-1 flex flex-col border-t lg:border-t-0 border-white/10">
            {/* Compact Chat Header */}
            <div className="p-3 border-b border-white/10">
              <h4 className="text-sm font-semibold text-white">Live Conversation</h4>
              <p className="text-gray-400 text-xs">Real-time {activeDemo.industry.toLowerCase()} interaction</p>
            </div>
            
            {/* Chat Container */}
            <div 
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent"
            >
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className={`w-12 h-12 bg-gradient-to-r ${activeDemo.gradient} opacity-20 rounded-full flex items-center justify-center mx-auto mb-3 border border-white/30`}>
                      <div className="w-6 h-6">
                        {activeDemo.icon}
                      </div>
                    </div>
                    <p className="text-gray-300 font-medium text-sm">Start demo to see conversation</p>
                    <p className="text-gray-500 text-xs mt-1">Experience {activeDemo.persona.name} in action</p>
                  </div>
                </div>
              ) : (
                messages.map((message, index) => (
                  <div 
                    key={message.timestamp}
                    className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className={`max-w-xs px-3 py-2 rounded-xl ${
                      message.sender === 'user' 
                        ? `bg-gradient-to-r ${activeDemo.gradient} text-white shadow-lg` 
                        : 'bg-white/5 backdrop-blur-xl border border-white/10 text-white'
                    }`}>
                      <p className="text-xs leading-relaxed">{message.text}</p>
                      <div className={`text-xs mt-1 opacity-70 ${
                        message.sender === 'agent' ? 'text-gray-400 font-semibold' : 'text-white/80'
                      }`}>
                        {message.sender === 'agent' ? activeDemo.persona.name : 'You'}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        
        {/* Compact Footer */}
        <div className="relative z-10 px-4 py-2 border-t border-white/10 bg-black/20">
          <div className="flex items-center justify-center">
            <p className="text-xs text-gray-500 text-center">
              🎤 {isDemoMode ? 'Demo active' : 'Live AI agent'} • Powered by Crel AI
            </p>
            {activeDemo.agentId === 'your-agent-id-here' && (
              <p className="text-xs text-yellow-400 px-2 py-1 bg-yellow-500/10 rounded-lg border border-yellow-500/20 ml-3">
                ⚠️ Configure Agent ID for live functionality
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiDemo; 